import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useShippingMethods } from '../hooks/useShippingMethods';
import { WEIGHT_BASED_METHOD, type ShippingOption } from '../lib/shipping';

interface Props {
  /** executeProtectedAction della dashboard: verifica admin prima di salvare. */
  executeProtectedAction: <T>(action: () => Promise<T>) => Promise<T | null>;
  logUpdateAction: (table: string, id: string, oldValues: Record<string, any>, newValues: Record<string, any>) => unknown;
}

interface Draft {
  price: string;
  freeFrom: string;
  active: boolean;
}

const toDraft = (m: ShippingOption): Draft => ({
  price: m.price.toFixed(2).replace('.', ','),
  freeFrom: m.freeFrom === null ? '' : m.freeFrom.toFixed(2).replace('.', ','),
  active: m.active,
});

/** "5,90" o "5.90" -> 5.9; vuoto -> null; non valido -> NaN */
const parseEuro = (value: string): number | null => {
  const v = value.trim().replace(',', '.');
  if (!v) return null;
  return /^\d+(\.\d{1,2})?$/.test(v) ? Number(v) : NaN;
};

const euroDraft = (value: number) => value.toFixed(2).replace('.', ',');

const kgLabel = (grams: number) => `${(grams / 1000).toLocaleString('it-IT')} kg`;

/** Prezzi e disponibilità dei metodi di spedizione (shipping_methods) e fasce di peso (shipping_weight_tiers). */
const ShippingSettings: React.FC<Props> = ({ executeProtectedAction, logUpdateAction }) => {
  const { methods, tiers, loading, error, updateMethod, updateTier } = useShippingMethods();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [tierDrafts, setTierDrafts] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    setDrafts(Object.fromEntries(methods.map((m) => [m.value, toDraft(m)])));
  }, [methods]);

  useEffect(() => {
    setTierDrafts(Object.fromEntries(tiers.map((t) => [t.max_grams, euroDraft(t.price)])));
  }, [tiers]);

  const tiersDirty = tiers.some((t) => tierDrafts[t.max_grams] !== euroDraft(t.price));

  const saveTiers = async () => {
    const changed = tiers.filter((t) => tierDrafts[t.max_grams] !== euroDraft(t.price));
    for (const t of changed) {
      const price = parseEuro(tierDrafts[t.max_grams] ?? '');
      if (price === null || Number.isNaN(price)) {
        toast.error(`Fino a ${kgLabel(t.max_grams)}: inserisci un prezzo valido, es. 6,90`);
        return;
      }
    }

    setSaving('tiers');
    await executeProtectedAction(async () => {
      try {
        for (const t of changed) {
          const price = parseEuro(tierDrafts[t.max_grams])!;
          await updateTier(t.max_grams, price);
          await logUpdateAction('shipping_weight_tiers', String(t.max_grams), { price: t.price }, { price });
        }
        toast.success('Prezzi a peso salvati');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(null);
  };

  const setDraft = (code: string, changes: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [code]: { ...prev[code], ...changes } }));

  const save = async (m: ShippingOption) => {
    const draft = drafts[m.value];
    const price = parseEuro(draft.price);
    const freeFrom = parseEuro(draft.freeFrom);
    if ((price === null || Number.isNaN(price)) && m.value !== WEIGHT_BASED_METHOD) {
      toast.error(`${m.label}: inserisci un prezzo valido, es. 5,90`);
      return;
    }
    if (Number.isNaN(freeFrom) || freeFrom === 0) {
      toast.error(`${m.label}: la soglia di gratuità deve essere un importo, es. 49, oppure vuota`);
      return;
    }

    setSaving(m.value);
    await executeProtectedAction(async () => {
      try {
        // Il prezzo del metodo a peso non si usa: resta quello salvato.
        await updateMethod(m.value, { price: price ?? m.price, free_from: freeFrom, active: draft.active });
        await logUpdateAction(
          'shipping_methods',
          m.value,
          { price: m.price, free_from: m.freeFrom, active: m.active },
          { price: price ?? m.price, free_from: freeFrom, active: draft.active }
        );
        toast.success(`${m.label}: salvato`);
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(null);
  };

  return (
    <section className="overflow-hidden rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40">
      <div className="border-b border-border px-6 py-5">
        <h2 className="font-display text-xl font-semibold text-foreground">Spedizioni</h2>
        <p className="mt-1 text-sm leading-[22px] text-muted-foreground">
          Prezzi IVA inclusa. "Gratis da" è il subtotale prodotti oltre il quale la spedizione non si paga: lascialo
          vuoto per non offrirla mai gratis. I nuovi prezzi valgono subito per carrello e pagamento.
        </p>
      </div>

      {loading && <div className="px-6 py-5 text-sm text-muted-foreground">Caricamento…</div>}
      {error && <div className="px-6 py-5 text-sm text-danger">{error}</div>}

      {methods.map((m) => {
        const draft = drafts[m.value];
        if (!draft) return null;
        const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(m));
        return (
          <div key={m.value} className="flex flex-wrap items-end gap-3 border-b border-border px-6 py-4 last:border-b-0">
            <div className="min-w-[160px] flex-1">
              <div className="text-sm font-semibold text-foreground">{m.label}</div>
              <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  className="accent-primary"
                  checked={draft.active}
                  onChange={(e) => setDraft(m.value, { active: e.target.checked })}
                />
                Disponibile per i clienti
              </label>
            </div>
            {m.value === WEIGHT_BASED_METHOD ? (
              <div className="flex h-10 items-center text-xs text-muted-foreground">Prezzo a peso, vedi sotto</div>
            ) : (
              <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
                Prezzo €
                <input
                  inputMode="decimal"
                  value={draft.price}
                  onChange={(e) => setDraft(m.value, { price: e.target.value })}
                  className="h-10 w-24 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
                />
              </label>
            )}
            <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
              Gratis da €
              <input
                inputMode="decimal"
                value={draft.freeFrom}
                placeholder="mai"
                onChange={(e) => setDraft(m.value, { freeFrom: e.target.value })}
                className="h-10 w-24 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
              />
            </label>
            <button
              onClick={() => save(m)}
              disabled={!dirty || saving !== null}
              className="h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {saving === m.value ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        );
      })}

      {tiers.length > 0 && (
        <div className="border-t border-border px-6 py-5">
          <h3 className="text-sm font-semibold text-foreground">Spedizione standard: prezzi a peso</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Peso del pacco imballato, stimato dal miele nel carrello più vasetti e scatola. Oltre l'ultima fascia si
            pagano più pacchi.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            {tiers.map((t) => (
              <label key={t.max_grams} className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
                Fino a {kgLabel(t.max_grams)} €
                <input
                  inputMode="decimal"
                  value={tierDrafts[t.max_grams] ?? ''}
                  onChange={(e) => setTierDrafts((prev) => ({ ...prev, [t.max_grams]: e.target.value }))}
                  className="h-10 w-24 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
                />
              </label>
            ))}
            <button
              onClick={saveTiers}
              disabled={!tiersDirty || saving !== null}
              className="h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {saving === 'tiers' ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ShippingSettings;
